import React from 'react';
import { css } from '@emotion/react';
import Snackbar from '@/components/Snackbar';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Body1, TabCard } from '../blocks';
import {
    AddPill,
    DirtyDot,
    EmptyNote,
    HeaderActions,
    PillRow,
    ReadOnlyPill,
    Section,
    SectionHeader,
    SectionNote,
    SectionTitle,
    TagPill,
    TypeCount,
} from './blocks';
import { useCustomTags } from './hooks';
import TagEditModal from './TagEditModal';
import { typeLabels } from './utils';

const cardPadding = css`
    padding: 2rem;
`;

const Header = ({ title, note, children }) => (
    <SectionHeader>
        <SectionTitle>{title}</SectionTitle>
        {note && <SectionNote>{note}</SectionNote>}
        {children && <HeaderActions>{children}</HeaderActions>}
    </SectionHeader>
);

/**
 * `bare` renders the section without its own card surface, for callers that already provide
 * one (the corporate shipper Tariffs & Tags tab). The Account pages drop it in unwrapped, so
 * the default keeps the TabCard they have always relied on.
 */
const CustomTags = ({ bare = false }) => {
    const { state, callbacks } = useCustomTags();
    const { tags, orgTags, user_id, loading, dirty, dirtyIds, notification, editing, deleting } = state;
    const Surface = bare ? React.Fragment : TabCard;
    const surfaceProps = bare ? {} : { css: cardPadding };

    return (
        <>
            <Snackbar open={!!notification.message} handleClose={callbacks.clearNotification} {...notification} />

            <Surface {...surfaceProps}>
                <Section>
                    <Header title="Tags" note={dirty ? '· unsaved changes' : undefined}>
                        <PrimaryButton disabled={loading || !dirty} onClick={callbacks.save}>
                            Save tags
                        </PrimaryButton>
                    </Header>

                    <PillRow>
                        {tags.map((tag) => {
                            const types = typeLabels(tag);
                            return (
                                <TagPill
                                    key={tag.tag_id}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => callbacks.openEditor(tag)}
                                    title={types ? `Types: ${types}` : 'No types set'}
                                >
                                    {dirtyIds.has(tag.tag_id) && <DirtyDot />}
                                    {tag.tag || 'Untitled tag'}
                                    {types && <TypeCount>{types}</TypeCount>}
                                </TagPill>
                            );
                        })}
                        <AddPill type="button" disabled={loading} onClick={callbacks.addTag}>
                            + Add tag
                        </AddPill>
                    </PillRow>

                    {orgTags.length > 0 && (
                        <>
                            <Header title="Organization tags" note="· managed by your organization — read only" />
                            <PillRow>
                                {orgTags.map((tag) => {
                                    const types = typeLabels(tag);
                                    return (
                                        <ReadOnlyPill
                                            key={tag.tag_id}
                                            title={types ? `Types: ${types}` : 'No types set'}
                                        >
                                            {tag.tag}
                                            {types && <TypeCount>{types}</TypeCount>}
                                        </ReadOnlyPill>
                                    );
                                })}
                            </PillRow>
                        </>
                    )}

                    {!tags.length && !orgTags.length && <EmptyNote>No tags yet — add one to get started.</EmptyNote>}
                </Section>
            </Surface>

            <TagEditModal
                tag={editing}
                user_id={user_id}
                loading={loading}
                onChange={callbacks.stage}
                onDelete={callbacks.askDelete}
                onClose={callbacks.closeEditor}
            />

            <NavResponsiveModal open={!!deleting} onClose={callbacks.cancelDelete}>
                <ModalHeader title="Delete Tag?" onClose={callbacks.cancelDelete} />
                <ModalContent>
                    <Body1>Are you sure you want to delete this tag?</Body1>
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={callbacks.cancelDelete}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={callbacks.confirmDelete}>Delete</PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>
        </>
    );
};

export default CustomTags;
